import { toSupportedLanguage, type Language } from '@/shared/i18n/languages';

import type { Currency, ProductRowInput, ProductTotals, RowAmounts } from '../api';

const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * The money of a product row — the same arithmetic as the server (`ProductMath`): gross = price × quantity, discount in
 * percent, VAT included in the price (tax = net × rate / (100 + rate)) or added (tax = net × rate / 100); every amount
 * rounded to kopecks per row.
 */
export function rowAmounts(
  row: Pick<ProductRowInput, 'price' | 'quantity' | 'discountPercent' | 'taxRate' | 'taxIncluded'>,
): RowAmounts {
  const gross = round(row.price * row.quantity);
  const discount = round((gross * row.discountPercent) / 100);
  const net = round(gross - discount);
  const rate = row.taxRate ?? 0;
  const tax =
    rate === 0 ? 0 : row.taxIncluded ? round((net * rate) / (100 + rate)) : round((net * rate) / 100);
  return { gross, discount, net, tax, total: row.taxIncluded ? net : round(net + tax) };
}

export function rowTotals(
  rows: readonly Pick<
    ProductRowInput,
    'price' | 'quantity' | 'discountPercent' | 'taxRate' | 'taxIncluded'
  >[],
): ProductTotals {
  const amounts = rows.map(rowAmounts);
  const sum = (pick: (a: RowAmounts) => number) => round(amounts.reduce((total, a) => total + pick(a), 0));
  return {
    count: rows.length,
    subtotal: sum((a) => a.gross),
    discount: sum((a) => a.discount),
    tax: sum((a) => a.tax),
    total: sum((a) => a.total),
  };
}

/** A price in another currency with the manual rates (1 unit = rate base units). */
export function convertPrice(
  price: number,
  from: string,
  to: string,
  currencies: readonly Currency[],
): number {
  if (from === to) return price;
  const rate = (code: string) => currencies.find((c) => c.code === code)?.rate ?? 1;
  return round((price * rate(from)) / rate(to));
}

/** VAT (ПДВ) rates of Ukraine the forms offer: 20 % standard, 14 % and 7 % reduced, 0 % (null = without VAT). */
export const VAT_RATES: readonly (number | null)[] = [null, 0, 7, 14, 20];

/** The hryvnia: the base currency of the CRM and the currency whose documents print the amount in words. */
export const HRYVNIA = 'UAH';

type Gender = 'male' | 'female';
type Forms = readonly [one: string, few: string, many: string];

/** "гривна / гривны / гривен" (and Ukrainian "гривня / гривні / гривень") for 1 / 2–4 / 5+ (and 11–14). */
export function pluralRu(n: number, forms: Forms): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return forms[2];
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

/** Numerals of a Slavic language: units by gender, teens, tens, hundreds and the scales (thousand is feminine). */
interface SlavicWords {
  ones: Record<Gender, readonly string[]>;
  teens: readonly string[];
  tens: readonly string[];
  hundreds: readonly string[];
  zero: string;
  scales: readonly { forms: Forms; gender: Gender }[];
  hryvnia: Forms;
  kopiyka: Forms;
}

const RUSSIAN: SlavicWords = {
  ones: {
    male: ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'],
    female: ['', 'одна', 'две', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'],
  },
  teens: [
    'десять',
    'одиннадцать',
    'двенадцать',
    'тринадцать',
    'четырнадцать',
    'пятнадцать',
    'шестнадцать',
    'семнадцать',
    'восемнадцать',
    'девятнадцать',
  ],
  tens: [
    '',
    '',
    'двадцать',
    'тридцать',
    'сорок',
    'пятьдесят',
    'шестьдесят',
    'семьдесят',
    'восемьдесят',
    'девяносто',
  ],
  hundreds: [
    '',
    'сто',
    'двести',
    'триста',
    'четыреста',
    'пятьсот',
    'шестьсот',
    'семьсот',
    'восемьсот',
    'девятьсот',
  ],
  zero: 'ноль',
  scales: [
    { forms: ['', '', ''], gender: 'female' },
    { forms: ['тысяча', 'тысячи', 'тысяч'], gender: 'female' },
    { forms: ['миллион', 'миллиона', 'миллионов'], gender: 'male' },
    { forms: ['миллиард', 'миллиарда', 'миллиардов'], gender: 'male' },
  ],
  hryvnia: ['гривна', 'гривны', 'гривен'],
  kopiyka: ['копейка', 'копейки', 'копеек'],
};

const UKRAINIAN: SlavicWords = {
  ones: {
    male: ['', 'один', 'два', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"],
    female: ['', 'одна', 'дві', 'три', 'чотири', "п'ять", 'шість', 'сім', 'вісім', "дев'ять"],
  },
  teens: [
    'десять',
    'одинадцять',
    'дванадцять',
    'тринадцять',
    'чотирнадцять',
    "п'ятнадцять",
    'шістнадцять',
    'сімнадцять',
    'вісімнадцять',
    "дев'ятнадцять",
  ],
  tens: [
    '',
    '',
    'двадцять',
    'тридцять',
    'сорок',
    "п'ятдесят",
    'шістдесят',
    'сімдесят',
    'вісімдесят',
    "дев'яносто",
  ],
  hundreds: [
    '',
    'сто',
    'двісті',
    'триста',
    'чотириста',
    "п'ятсот",
    'шістсот',
    'сімсот',
    'вісімсот',
    "дев'ятсот",
  ],
  zero: 'нуль',
  scales: [
    { forms: ['', '', ''], gender: 'female' },
    { forms: ['тисяча', 'тисячі', 'тисяч'], gender: 'female' },
    { forms: ['мільйон', 'мільйони', 'мільйонів'], gender: 'male' },
    { forms: ['мільярд', 'мільярди', 'мільярдів'], gender: 'male' },
  ],
  hryvnia: ['гривня', 'гривні', 'гривень'],
  kopiyka: ['копійка', 'копійки', 'копійок'],
};

/** Whole hryvnias and kopecks of an amount (rounded to kopecks; negative amounts are written as their absolute value). */
function split(amount: number): { units: number; kopecks: number } {
  const cents = Math.round(Math.abs(amount) * 100);
  return { units: Math.floor(cents / 100), kopecks: cents % 100 };
}

/** The groups of three digits of a number, the lowest first. */
function triads(value: number): number[] {
  const parts: number[] = [];
  for (let rest = value; rest > 0; rest = Math.floor(rest / 1000)) parts.push(rest % 1000);
  return parts;
}

function slavicTriad(n: number, gender: Gender, words: SlavicWords): string[] {
  const result = [words.hundreds[Math.floor(n / 100)] ?? ''];
  const rest = n % 100;
  if (rest >= 10 && rest < 20) result.push(words.teens[rest - 10] ?? '');
  else result.push(words.tens[Math.floor(rest / 10)] ?? '', words.ones[gender][rest % 10] ?? '');
  return result.filter(Boolean);
}

function slavicInWords(amount: number, words: SlavicWords): string {
  const { units, kopecks } = split(amount);
  const parts = triads(units);
  const text: string[] = [];
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i] ?? 0;
    const scale = words.scales[i];
    if (part === 0 || !scale) continue;
    text.push(...slavicTriad(part, scale.gender, words));
    if (i > 0) text.push(pluralRu(part, scale.forms));
  }
  const number = text.length > 0 ? text.join(' ') : words.zero;
  return `${number} ${pluralRu(units, words.hryvnia)} ${String(kopecks).padStart(2, '0')} ${pluralRu(kopecks, words.kopiyka)}`;
}

const EN_ONES = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const EN_TEENS = [
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
const EN_SCALES = ['', 'thousand', 'million', 'billion'];

function englishTriad(n: number): string[] {
  const result: string[] = [];
  if (n >= 100) result.push(EN_ONES[Math.floor(n / 100)] ?? '', 'hundred');
  const rest = n % 100;
  if (rest >= 10 && rest < 20) result.push(EN_TEENS[rest - 10] ?? '');
  else if (rest >= 20)
    result.push(
      rest % 10 === 0
        ? (EN_TENS[rest / 10] ?? '')
        : `${EN_TENS[Math.floor(rest / 10)] ?? ''}-${EN_ONES[rest % 10] ?? ''}`,
    );
  else if (rest > 0) result.push(EN_ONES[rest] ?? '');
  return result;
}

function englishInWords(amount: number): string {
  const { units, kopecks } = split(amount);
  const parts = triads(units);
  const text: string[] = [];
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i] ?? 0;
    if (part === 0) continue;
    text.push(...englishTriad(part));
    if (i > 0) text.push(EN_SCALES[i] ?? '');
  }
  const number = text.length > 0 ? text.join(' ') : 'zero';
  return `${number} ${units === 1 ? 'hryvnia' : 'hryvnias'} ${String(kopecks).padStart(2, '0')} ${kopecks === 1 ? 'kopiyka' : 'kopiykas'}`;
}

/**
 * A hryvnia amount in words, as printed on invoices, in the document's language: "Двадцать три тысячи пятьсот гривен
 * 50 копеек", "Двадцять три тисячі п'ятсот гривень 50 копійок", "Twenty-three thousand five hundred hryvnias 50
 * kopiykas" (kopecks in digits). Amounts up to 999 999 999 999,99.
 */
export function hryvniasInWords(amount: number, language: Language): string {
  const text =
    language === 'en'
      ? englishInWords(amount)
      : slavicInWords(amount, language === 'uk' ? UKRAINIAN : RUSSIAN);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The amount in words of a document: hryvnia documents only (null for other currencies). */
export function amountInWords(amount: number, currencyCode: string, language: Language): string | null {
  return currencyCode === HRYVNIA ? hryvniasInWords(amount, language) : null;
}

/**
 * The language a quote or invoice is printed in: chosen on the print page (`?lang=`), else Ukrainian for hryvnia
 * documents (the state language of business documents in Ukraine, with the amount in words), else the user's language.
 */
export function documentLanguage(chosen: string | null, currencyCode: string, uiLanguage: string): Language {
  return (
    toSupportedLanguage(chosen) ??
    (currencyCode === HRYVNIA ? 'uk' : (toSupportedLanguage(uiLanguage) ?? 'ru'))
  );
}
